import React, { useState } from 'react';
import { Search, User, CreditCard, Calendar, FileText, ChevronDown, ChevronUp, MessageCircle, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';

export const Help: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const categories = [
    { icon: User, title: 'Account & Profile', desc: 'Managing your personal details' },
    { icon: Calendar, title: 'Appointments', desc: 'Booking, rescheduling, & canceling' },
    { icon: CreditCard, title: 'Billing & Insurance', desc: 'Payments, invoices, & coverage' },
    { icon: FileText, title: 'Medical Records', desc: 'Accessing prescriptions & labs' },
  ];

  const faqs = [
    {
      category: 'Account',
      question: "How do I reset my password?",
      answer: "Open the app login screen and tap 'Forgot Password'. Enter your registered email address, and we will send you a link to reset your password immediately."
    },
    {
      category: 'Appointments',
      question: "Can I cancel an appointment?",
      answer: "Yes, you can cancel up to 4 hours before the scheduled time for a full refund. Go to 'My Appointments' in the app, select the booking, and tap 'Cancel'."
    },
    {
      category: 'Billing',
      question: "Do you accept insurance?",
      answer: "We accept most major insurance providers in Kenya and South Africa. You can add your insurance details in the 'Payment Methods' section of your profile to check eligibility."
    },
    {
      category: 'Records',
      question: "How do I download my prescription?",
      answer: "After your consultation, your doctor will upload the prescription. Go to the 'Records' tab, select the relevant visit, and tap 'Download PDF' to save or print it."
    },
    {
      category: 'Technical',
      question: "The video call isn't connecting.",
      answer: "Please ensure you have a stable internet connection and have granted the app permission to access your camera and microphone in your device settings."
    }
  ];

  const filteredFaqs = faqs.filter(faq => 
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()) || 
    faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="pt-20 min-h-screen bg-gray-50">
      {/* Hero Search */}
      <div className="bg-secondary text-white py-20 px-6 relative overflow-hidden">
         {/* Background decor */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/10 rounded-full blur-[100px] translate-x-1/3 -translate-y-1/3"></div>
        
        <div className="container mx-auto relative z-10 text-center max-w-2xl">
            <h1 className="text-3xl md:text-5xl font-bold mb-6">How can we help you?</h1>
            <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input 
                    type="text" 
                    placeholder="Search for answers (e.g. 'refund', 'prescription')"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full py-4 pl-12 pr-6 rounded-full text-gray-900 focus:outline-none focus:ring-4 focus:ring-primary/30 transition-shadow shadow-xl"
                />
            </div>
        </div>
      </div>

      <div className="container mx-auto px-6 -mt-8 relative z-20">
        {/* Categories */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
            {categories.map((cat, i) => (
                <div key={i} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-lg hover:-translate-y-1 transition-all cursor-pointer">
                    <div className="w-10 h-10 bg-blue-50 text-primary rounded-xl flex items-center justify-center mb-4">
                        <cat.icon size={20} />
                    </div>
                    <h3 className="font-bold text-secondary">{cat.title}</h3>
                    <p className="text-sm text-gray-500 mt-1">{cat.desc}</p>
                </div>
            ))}
        </div>

        {/* FAQs */}
        <div className="max-w-3xl mx-auto mb-20">
            <h2 className="text-2xl font-bold text-secondary mb-8">Frequently Asked Questions</h2>
            
            {filteredFaqs.length > 0 ? (
                <div className="space-y-4">
                    {filteredFaqs.map((faq, index) => (
                        <div key={index} className="bg-white border border-gray-200 rounded-xl overflow-hidden transition-all hover:border-primary/30">
                            <button 
                                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                                className="w-full flex items-center justify-between p-5 text-left"
                            >
                                <span className="font-semibold text-secondary">{faq.question}</span>
                                {openIndex === index ? <ChevronUp size={18} className="text-primary" /> : <ChevronDown size={18} className="text-gray-400" />}
                            </button>
                            {openIndex === index && (
                                <div className="px-5 pb-5 text-gray-600 text-sm leading-relaxed border-t border-gray-50 pt-4">
                                    {faq.answer}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            ) : (
                <div className="text-center py-10 bg-white rounded-2xl border border-dashed border-gray-300">
                    <p className="text-gray-500">No results found for "{searchQuery}"</p>
                </div>
            )}
        </div>

        {/* Contact CTA */}
        <div className="max-w-4xl mx-auto bg-white rounded-[2rem] p-8 md:p-12 border border-gray-100 shadow-xl text-center mb-24">
            <h3 className="text-2xl font-bold text-secondary mb-4">Still need help?</h3>
            <p className="text-gray-500 mb-8 max-w-xl mx-auto">Our support team is available 24/7 to assist you with any technical issues or booking inquiries.</p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-primary text-white px-8 py-3 rounded-full font-bold hover:bg-primary-light transition-colors">
                    <MessageCircle size={18} />
                    Chat Support
                </Link>
                <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-white text-secondary border border-gray-200 px-8 py-3 rounded-full font-bold hover:bg-gray-50 transition-colors">
                    <Mail size={18} />
                    Email Us
                </Link>
            </div>
        </div>
      </div>
    </div>
  );
};